import { useQuery } from "@tanstack/react-query";
import type { LatLngTuple } from "leaflet";
import { useEffect, useMemo, useRef } from "react";
import { useFetchWrapper } from "modules/common/hooks/useFetchWrapper";
import { API_ROUTES } from "utils/api-route.constants";
import { LGS } from "modules/lgs/model/lgs";
import { EDGE_LIVE_DATA_CONFIG } from "modules/common/react-query-configs";
import { useToast } from "modules/common/components/toast/useToast";

function useWotcLGS(
  coords: LatLngTuple,
  searchCity?: string,
  options?: { enabled?: boolean }
) {
  const fetch = useFetchWrapper();
  const toast = useToast();
  const lastEmptyToastKey = useRef<string | null>(null);
  const lng = coords[0];
  const lat = coords[1];

  const {
    data,
    isSuccess,
    isLoading,
    isFetching,
    isPending,
    isError,
    isPlaceholderData,
  } = useQuery({
    // City display text does not affect the API request. Coordinates are the
    // complete result identity, so labels must not split the same cache entry.
    queryKey: ["lgs", lng, lat],
    queryFn: async () => fetchStores(),
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
    enabled: options?.enabled ?? true,
    // Store coordinates are static, but userCount is hydrated live by search.
    ...EDGE_LIVE_DATA_CONFIG,
  });

  useEffect(() => {
    if (!searchCity || isError || isFetching || isPlaceholderData || !data) {
      return;
    }

    const emptySearchKey = `${lng}:${lat}:${searchCity}`;
    if (data.length > 0) {
      lastEmptyToastKey.current = null;
      return;
    }
    if (lastEmptyToastKey.current === emptySearchKey) return;

    // Query lifecycle refetches may repeat an empty response. Remember the
    // selected coordinate+label so those refreshes do not repeat the UX event.
    lastEmptyToastKey.current = emptySearchKey;
    toast.warning("No stores found in:", {
      description: searchCity,
      id: `no-stores-${emptySearchKey}`,
    });
  }, [
    data,
    isError,
    isFetching,
    isPlaceholderData,
    lat,
    lng,
    searchCity,
    toast,
  ]);

  const shopLocations = useMemo(
    () => data?.map((shop) => [shop.latitude, shop.longitude]),
    [data]
  );

  return {
    data,
    isLoading,
    isPending,
    isFetching,
    isSuccess,
    shopLocations,
    isError,
    isPlaceholderData,
  };

  async function fetchStores() {
    const params = `?lat=${lat}&lng=${lng}`;
    const response: LGS[] = await fetch.publicGet(
      API_ROUTES.LGS.GET + params,
      null
    );
    return response;
  }
}

export default useWotcLGS;
